import { QueryClient, InvalidateQueryFilters } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

/**
 * Centralized cache invalidation utilities
 * These functions ensure comprehensive cache invalidation across related data
 */

// Internal types for cached data shapes
interface CachedTask {
  task_id: number;
  status: string;
  is_dismissed: boolean;
}

interface CachedDocument {
  id: number | string;
  [key: string]: unknown;
}

export interface InvalidationContext {
  estateId?: number;
  userId?: string;
  taskId?: number;
  documentId?: string;
  contactId?: number;
}

/**
 * Invalidate all task-related queries
 * Call this after any task mutations (create, update, delete, dismiss)
 */
export const invalidateTaskQueries = async (
  queryClient: QueryClient,
  context: InvalidationContext = {},
) => {
  const { estateId, taskId } = context;

  // Core task queries
  await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });

  if (estateId) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.person(estateId) });
  }

  if (taskId && estateId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.tasks.single(taskId, estateId),
    });
  }

  // Related queries that depend on task data
  await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.featured });
  await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.buckets });
  await queryClient.invalidateQueries({ queryKey: queryKeys.user.settings });

  // Person metadata may include task completion stats
  if (estateId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.person.metadata(estateId),
    });
  }
};

/**
 * Invalidate all user-related queries
 * Call this after user profile updates, preferences changes, etc.
 */
export const invalidateUserQueries = async (
  queryClient: QueryClient,
  context: InvalidationContext = {},
) => {
  const { userId } = context;

  // Core user queries
  await queryClient.invalidateQueries({ queryKey: queryKeys.user.current });
  await queryClient.invalidateQueries({ queryKey: queryKeys.user.settings });
  await queryClient.invalidateQueries({ queryKey: ["userNotifications"] });

  if (userId) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(userId) });
  }

  // Auth-related queries
  await queryClient.invalidateQueries({ queryKey: queryKeys.auth.user });
  await queryClient.invalidateQueries({ queryKey: queryKeys.features.subscription });
};

/**
 * Invalidate all document-related queries
 * Call this after document uploads, deletions, status changes
 */
export const invalidateDocumentQueries = async (
  queryClient: QueryClient,
  context: InvalidationContext = {},
) => {
  const { estateId, documentId } = context;

  // Core document queries - invalidate all queries starting with "documents"
  await queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });

  // Also invalidate the actual query key format used by useDocumentStorage: ["documents", estateId, ...]
  if (estateId) {
    await queryClient.invalidateQueries({
      queryKey: ["documents", estateId],
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.documents.person(estateId),
    });
    // Invalidate userDocumentMetadata queries
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return key[0] === "userDocumentMetadata" && key[2] === estateId;
      },
    });
  }

  if (documentId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.documents.single(documentId),
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.documents.metadata(documentId),
    });
    // Invalidate specific document metadata query
    await queryClient.invalidateQueries({
      queryKey: ["userDocumentMetadata", parseInt(documentId)],
    });
  }

  // Tasks may depend on document status
  if (estateId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.tasks.person(estateId),
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.featured });
  }
};

/**
 * Invalidate the document list and the key document checks after an upload
 */
export const invalidateUploadedDocumentQueries = (queryClient: QueryClient, estateId: number) => {
  void queryClient.invalidateQueries({ queryKey: ["documents", estateId] });
  void queryClient.invalidateQueries({ queryKey: ["hasDeathCert", estateId] });
  void queryClient.invalidateQueries({ queryKey: ["hasAuthorityDocument", estateId] });
  void queryClient.invalidateQueries({ queryKey: ["keyDocCount", estateId] });
};

/**
 * Invalidate all person-related queries
 * Call this after person creation, profile updates, etc.
 */
export const invalidatePersonQueries = async (
  queryClient: QueryClient,
  context: InvalidationContext = {},
) => {
  const { estateId } = context;

  // Core person queries
  await queryClient.invalidateQueries({ queryKey: queryKeys.person.all });

  if (estateId) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.person.single(estateId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.person.metadata(estateId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.person.invitations(estateId) });

    // Related data
    await queryClient.invalidateQueries({ queryKey: queryKeys.contacts.person(estateId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.documents.person(estateId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.person(estateId) });
  }
};

/**
 * Invalidate all contact-related queries
 * Call this after contact creation, updates, deletions
 */
export const invalidateContactQueries = async (
  queryClient: QueryClient,
  context: InvalidationContext = {},
) => {
  const { estateId, contactId } = context;

  // Core contact queries
  await queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });

  if (estateId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.contacts.person(estateId),
    });
  }

  if (contactId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.contacts.single(contactId),
    });
  }

  // Person metadata may include contact counts
  if (estateId) {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.person.metadata(estateId),
    });
  }
};

/**
 * Invalidate admin-related queries
 * Call this after admin actions, feature flag changes, etc.
 */
export const invalidateAdminQueries = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: queryKeys.admin.users });
  await queryClient.invalidateQueries({ queryKey: queryKeys.admin.requests });
  await queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboard });
  await queryClient.invalidateQueries({ queryKey: queryKeys.admin.featureFlags });
  await queryClient.invalidateQueries({ queryKey: queryKeys.features.flags });
};

/**
 * Nuclear option: invalidate everything
 * Use sparingly, only for major state changes like login/logout
 */
export const invalidateAllQueries = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries();
};

/**
 * Optimistic update helpers
 */
export const optimisticUpdates = {
  /**
   * Optimistically dismiss a task
   */
  dismissTask: (queryClient: QueryClient, taskId: number, estateId: number) => {
    queryClient.setQueryData<CachedTask[] | undefined>(
      queryKeys.tasks.person(estateId),
      (oldTasks) =>
        oldTasks?.map((task) => (task.task_id === taskId ? { ...task, is_dismissed: true } : task)),
    );
  },

  /**
   * Optimistically update task status
   */
  updateTaskStatus: (
    queryClient: QueryClient,
    taskId: number,
    estateId: number,
    status: string,
  ) => {
    queryClient.setQueryData<CachedTask | undefined>(
      queryKeys.tasks.single(taskId, estateId),
      (oldTask) => (oldTask ? { ...oldTask, status } : oldTask),
    );

    queryClient.setQueryData<CachedTask[] | undefined>(
      queryKeys.tasks.person(estateId),
      (oldTasks) => oldTasks?.map((task) => (task.task_id === taskId ? { ...task, status } : task)),
    );
  },

  /**
   * Optimistically add document
   */
  addDocument: (queryClient: QueryClient, document: CachedDocument, estateId: number) => {
    queryClient.setQueryData<CachedDocument[] | undefined>(
      queryKeys.documents.person(estateId),
      (oldDocs) => (oldDocs ? [document, ...oldDocs] : [document]),
    );
  },
};

/**
 * Cache debugging utilities (development only)
 */
export const cacheDebug = {
  logCacheState: (queryClient: QueryClient, prefix = "Cache Debug") => {
    if (import.meta.env.DEV) {
      console.group(prefix);
      const cache = queryClient.getQueryCache();
      cache.getAll().forEach((query) => {
        console.log(`${query.queryKey.join(" -> ")}: `, query.state);
      });
      console.groupEnd();
    }
  },

  invalidationTracker: (queryClient: QueryClient) => {
    if (import.meta.env.DEV) {
      const originalInvalidate = queryClient.invalidateQueries.bind(queryClient);
      queryClient.invalidateQueries = function (filters?: InvalidateQueryFilters) {
        return originalInvalidate(filters);
      };
    }
  },
};
